import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import "./securityLogs.css";

const TPA_COLUMNS = ["Third Party", "Account", "Added at", "Operation"];

function parseConnectedAccountsPayload(result) {
  const raw = result?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.accounts)) return raw.accounts;
  return [];
}

function formatAddedAt(value) {
  if (!value) return "--";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function mapConnectedAccountRow(item, idx) {
  const type = String(item?.type || "").toLowerCase();
  const status = String(item?.status || "").toLowerCase();
  const thirdParty =
    item?.third_party ||
    (type === "google" ? "Google" : type === "apple" ? "Apple" : type || "--");
  const account = item?.account || item?.email || "--";
  const addedAt = formatAddedAt(item?.added_at || item?.addedAt || null);
  const opRaw = String(item?.operation || "").trim();
  const operation =
    opRaw === "Unbind" || opRaw === "Bind"
      ? opRaw
      : status === "connected" || String(opRaw).toLowerCase() === "disconnect"
        ? "Unbind"
        : "Bind";

  return {
    id: type || item?._id || idx,
    type: type || null,
    status: status || null,
    thirdParty,
    account,
    addedAt,
    operation,
  };
}

/**
 * Third Party Account Access Management
 * Unbind: confirm modal → POST connect-disconnect-account (no OTP; password checked on backend).
 * Route: /user_profile/security/thirdPartyAccess
 */
const ThirdPartyAccess = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busyType, setBusyType] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);

  const isEmpty = rows.length === 0;
  const colCount = TPA_COLUMNS.length;

  const fetchConnectedAccounts = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.getConnectedAccounts();
      if (!result?.success) {
        setRows([]);
        if (result?.message) alertErrorMessage(result.message);
        return;
      }
      const list = parseConnectedAccountsPayload(result);
      const mapped = list
        .map(mapConnectedAccountRow)
        .filter((row) => row.status === "connected" || row.operation === "Unbind");
      setRows(mapped);
    } catch (e) {
      setRows([]);
      alertErrorMessage(e?.message || "Could not load connected accounts.");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  useEffect(() => {
    void fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  const openUnbindConfirm = (row) => {
    const accountType = String(row?.type || "").toLowerCase();
    if (!accountType || !["google", "apple"].includes(accountType)) {
      alertErrorMessage("Unknown account type.");
      return;
    }
    if (busyType) return;
    setConfirmRow(row);
  };

  const closeUnbindConfirm = () => {
    if (busyType) return;
    setConfirmRow(null);
  };

  /** Existing API: action disconnected + account_type — no OTP. */
  const confirmUnbind = async () => {
    const row = confirmRow;
    if (!row) return;
    const accountType = String(row.type || "").toLowerCase();
    if (!accountType) return;

    setBusyType(accountType);
    LoaderHelper.loaderStatus(true);
    try {
      const sendRes = await AuthService.sendOtpForConnectDisconnectAccount("email");
      if (!sendRes?.success) {
        alertErrorMessage(
          sendRes?.message ||
          "Could not send verification OTP. Enable email security or manage this from Account Connections."
        );
        return;
      }
      alertSuccessMessage(sendRes.message || "Verification code sent to your email.");

      const code = window.prompt(
        `Enter the email OTP to unbind ${confirmLabel}:`
      );
      if (code == null || !String(code).trim()) {
        return;
      }
      const res = await AuthService.connectDisconnectAccount({
        action: "disconnected",
        account_type: accountType,
      });

      if (!res?.success) {
        alertErrorMessage(
          res?.message || "Could not remove third-party access."
        );
        return;
      }
      alertSuccessMessage(res.message || "Access removed successfully.");
      setConfirmRow(null);
      await fetchConnectedAccounts();
    } catch (e) {
      alertErrorMessage(e?.message || "Could not remove third-party access.");
    } finally {
      setBusyType(null);
      LoaderHelper.loaderStatus(false);
    }
  };

  const confirmLabel = confirmRow?.thirdParty || "this";

  return (
    <main className="slg-page" aria-labelledby="tpa-title">
      <nav className="slg-page__crumbs" aria-label="Breadcrumb">
        <ol className="slg-page__crumbList">
          <li className="slg-page__crumbItem">
            <button type="button" className="slg-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="slg-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="slg-page__crumbItem slg-page__crumbItem--active" aria-current="page">
            Third Party Account Access Management
          </li>
        </ol>
      </nav>

      <div className="security_section_bl">
        <h1 id="tpa-title" className="slg-page__title">
          Third Party Account Access Management
        </h1>

        <p className="slg-page__hint" style={{ marginBottom: 16, color: "#8e939c", fontSize: 14 }}>
          Linked Google / Apple accounts. To connect a new account, use{" "}
          <button
            type="button"
            className="slg-page__crumbLink tpa-account-connections-link"
            onClick={() => navigate("/user_profile/security/accountConnections")}
          >
            Account Connections
          </button>
          .
        </p>

        <div className="slg-tableWrap slg-tableWrap--mobileCards">
          <div className="slg-tableScroll">
            <table className="slg-table">
              <thead>
                <tr>
                  {TPA_COLUMNS.map((label) => (
                    <th key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isEmpty ? (
                  <tr className="slg-table__emptyRow">
                    <td colSpan={colCount}>
                      <div className="slg-empty" aria-live="polite">
                        <img
                          src="/images/no-data.svg"
                          alt=""
                          width={120}
                          height={144}
                          className="spot_orders_empty_telescope"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td data-label={TPA_COLUMNS[0]}>
                        <span className="slg-cardVal">{row.thirdParty}</span>
                      </td>
                      <td data-label={TPA_COLUMNS[1]}>
                        <span className="slg-cardVal">{row.account}</span>
                      </td>
                      <td data-label={TPA_COLUMNS[2]}>
                        <span className="slg-cardVal">{row.addedAt}</span>
                      </td>
                      <td data-label={TPA_COLUMNS[3]}>
                        <button
                          type="button"
                          className="tf-sec-page__btn"
                          disabled={!!busyType}
                          onClick={() => openUnbindConfirm(row)}
                        >
                          {busyType === row.type ? "Please wait…" : "Unbind"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmRow
        ? createPortal(
            <div
              className="tpa-unbind-overlay"
              role="presentation"
              onClick={closeUnbindConfirm}
            >
              <div
                className="tpa-unbind-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tpa-unbind-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tpa-unbind-modal__close"
                  aria-label="Close"
                  disabled={!!busyType}
                  onClick={closeUnbindConfirm}
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
                <h2 id="tpa-unbind-title" className="tpa-unbind-modal__title">
                  Tip for Removing Access
                </h2>
                <p className="tpa-unbind-modal__text">
                  Once removed, you stop signing in the account with the third party account
                  {confirmLabel ? ` (${confirmLabel})` : ""}.
                </p>
                <div className="tpa-unbind-modal__actions">
                  <button
                    type="button"
                    className="tpa-unbind-modal__btn tpa-unbind-modal__btn--cancel"
                    disabled={!!busyType}
                    onClick={closeUnbindConfirm}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tpa-unbind-modal__btn tpa-unbind-modal__btn--confirm"
                    disabled={!!busyType}
                    onClick={() => void confirmUnbind()}
                  >
                    {busyType ? "Please wait…" : "Remove Access"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </main>
  );
};

export default ThirdPartyAccess;
